import React from 'react';
import { motion } from 'framer-motion';

const HowItWorks = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const steps = [
    {
      number: 1,
      icon: '📝',
      title: 'Create Account',
      description: 'Sign up with your email and basic information. It takes less than a minute.',
    },
    {
      number: 2,
      icon: '💰',
      title: 'Choose Amount',
      description: 'Select the digital currency and amount you want to purchase with transparent pricing.',
    },
    {
      number: 3,
      icon: '✅',
      title: 'Verify Details',
      description: 'Review the order summary including fees and agree to our terms.',
    },
    {
      number: 4,
      icon: '🚀',
      title: 'Receive Your Currency',
      description: 'Your chosen currency is delivered to your wallet within 24 hours.',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full filter blur-3xl opacity-20"
          animate={{ y: [0, -50, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">How It Works</h2>
          <p className="text-gray-400 text-lg">Get your digital currency in 4 simple steps</p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-4 gap-8 relative"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="hidden md:block absolute top-24 left-8 right-8 h-1 bg-gradient-to-r from-amber-500/20 via-amber-500/40 to-amber-500/20" />

          {steps.map((step, idx) => (
            <motion.div key={idx} variants={itemVariants} whileHover={{ y: -10 }} className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/15 to-amber-500/5 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />

              <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-amber-500/25 group-hover:border-amber-500/50 transition-all duration-300 h-full flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <motion.div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-xl relative z-10" whileHover={{ scale: 1.1 }}>
                    {step.number}
                  </motion.div>
                  {idx < steps.length - 1 && (
                    <motion.div className="hidden md:block text-amber-400/50" animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                      →
                    </motion.div>
                  )}
                </div>

                <div className="text-4xl mb-4">{step.icon}</div>

                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div className="text-center mt-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }}>
          <motion.button whileHover={{ scale: 1.05, boxShadow: '0 25px 50px rgba(251, 191, 36, 0.25)' }} whileTap={{ scale: 0.95 }} className="px-10 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold text-lg rounded-lg hover:from-amber-300 hover:to-amber-400 transition-all duration-300">
            Get Started Now
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
